/**
 * JobChip — a job card on the dispatch board (Wave 3 Part 2 / Quick 260615-uf9).
 *
 * When `draggable`, it is BOTH a dnd-kit draggable (id `job:<id>`) and a drop
 * target (id `jobtarget:<id>`) so dropping another job onto it inserts before it
 * (reorder / position). In-progress jobs render pinned + highlighted and are NOT
 * draggable. A click (no drag — 5px activation constraint) opens the detail modal.
 *
 * Quick 260616-f4r:
 *   #5 — scheduled-time urgency dot (overdue=red, due-soon=amber).
 *   #4 — optional `onContextMenu` (right-click + touch long-press) so the unassigned
 *        panel can show a "Quick assign" menu. Only passed for unassigned chips.
 *
 * Quick 260616-bd6 (#1): compact single-line layout — `[urgency clock] [site
 * name] [→ destination] [bbl] [status badge]`.
 *
 * Quick 260721 — dispatcher legibility pass:
 *   1. The in-progress outline/tint uses the job's REAL status color
 *      (statusColor, shared with StatusBadge) instead of a hardcoded blue.
 *   2. The site name gets priority for the row's flexible width — the
 *      destination collapses first (high flex-shrink), the BBL is fixed-but-tiny —
 *      so a typical site name (e.g. "TED 30-13N-8W") reads without truncating.
 *   3. The scattered per-span `title` tooltips are replaced by ONE rich Radix
 *      hover tooltip on the whole chip (Site / Operator / Status / Destination /
 *      Captured / Scheduled). No native `title` on the draggable element, so the
 *      hover trigger never fights dnd-kit's pointer-drag activation.
 *
 * Task #32 (Corbin's spec) — `variant="driver-card"` (the rows on a driver's card):
 *   - the status WORD badge is replaced by an icon: green dot = underway (any in-progress
 *     status), nothing = assigned but not started (PENDING), gray ring = unknown code;
 *   - URGENT is a red triangle icon, NOT STARTED a gray hourglass (no words);
 *   - no destination: the row shows the pickup only;
 *   - the job type and the REAL status ("Produced Water Haul · En route to pickup") head
 *     the hover tooltip, which also opens on keyboard focus, and are in the row's
 *     accessible name (screen-reader-only text), so nothing depends on seeing a color.
 * The Unassigned panel keeps the default variant (badge + destination) unchanged.
 */

import * as React from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Clock, Hourglass, TriangleAlert } from 'lucide-react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@lodex-pro/web-shared/src/components/ui/tooltip';
import type { DispatchJob } from '../jobs/use-jobs';
import { StatusBadge } from '../jobs/status-badge';
import { statusColor, statusFullLabel } from '../jobs/job-status.js';
import { formatTimestamp } from '../../lib/timestamp';
import { isInProgress, jobRowStatusIcon, jobRowSummary, jobUrgency } from './board-utils';
import {
  OPERATOR_NOTE_ACCENT,
  OPERATOR_NOTE_LABEL,
  hasOperatorRequestNote,
} from '../jobs/OperatorRequestNote';

export interface JobChipProps {
  readonly job: DispatchJob;
  readonly draggable: boolean;
  readonly onSelect: (jobId: string) => void;
  /** #4 — open the quick-assign menu at the given viewport point. */
  readonly onContextMenu?: (clientX: number, clientY: number, jobId: string) => void;
  /** Task #32 — `driver-card` rows use icons instead of words and drop the destination. */
  readonly variant?: 'driver-card' | 'panel';
}

const LONG_PRESS_MS = 500;

/** Visually hidden but read by screen readers (part of the row's accessible name). */
const SR_ONLY: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/** Task #32 — the status icon slot. Always 10px wide so site names line up down a card. */
function StatusIcon({
  statusCode,
  label,
}: {
  statusCode: string;
  label: string;
}): React.JSX.Element {
  const kind = jobRowStatusIcon(statusCode);
  const dot: React.CSSProperties = {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    boxSizing: 'border-box',
  };
  return (
    <span
      data-testid="job-status-icon"
      data-status-icon={kind}
      style={{ width: '10px', flexShrink: 0, display: 'inline-flex', justifyContent: 'center' }}
    >
      {kind === 'underway' && (
        <span
          role="img"
          aria-label={label}
          style={{ ...dot, backgroundColor: 'var(--color-success)' }}
        />
      )}
      {kind === 'other' && (
        <span
          role="img"
          aria-label={label}
          style={{ ...dot, border: '2px solid var(--color-text-tertiary)' }}
        />
      )}
    </span>
  );
}

/** One label/value row of the rich hover tooltip. */
function TipRow({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', gap: '8px', lineHeight: 1.5 }}>
      <span style={{ color: 'var(--color-text-tertiary)', minWidth: '68px', flexShrink: 0 }}>
        {label}
      </span>
      <span style={{ color: 'var(--color-text-primary)', fontWeight: 500, whiteSpace: 'pre-wrap' }}>
        {value}
      </span>
    </div>
  );
}

export function JobChip({
  job,
  draggable,
  onSelect,
  onContextMenu,
  variant = 'panel',
}: JobChipProps): React.JSX.Element {
  const onCard = variant === 'driver-card';
  const summary = jobRowSummary(job);
  const active = isInProgress(job);
  const urgency = jobUrgency(job.scheduledAt);
  const drag = useDraggable({
    id: `job:${job.id}`,
    data: { jobId: job.id, driverId: job.driverId },
    disabled: !draggable,
  });
  const drop = useDroppable({
    id: `jobtarget:${job.id}`,
    data: { driverId: job.driverId, beforeJobId: job.id },
    disabled: !draggable,
  });

  const setRefs = React.useCallback(
    (node: HTMLElement | null): void => {
      drag.setNodeRef(node);
      drop.setNodeRef(node);
    },
    [drag, drop],
  );

  // #4 — long-press (touch / iPad) opens the same quick-assign menu as right-click.
  const longPressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearLongPress = React.useCallback((): void => {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const urgencyColor =
    urgency === 'overdue'
      ? 'var(--color-danger)'
      : urgency === 'due-soon'
        ? 'var(--color-warning)'
        : null;

  // Quick 260721 (#1) — the in-progress outline/tint now uses the job's real
  // status color (same palette as its StatusBadge) instead of a hardcoded blue.
  const statusC = statusColor(job.statusCode);

  const style: React.CSSProperties = {
    position: 'relative',
    padding: '4px 8px',
    borderRadius: '6px',
    border: active ? `1px solid ${statusC.fg}` : '1px solid var(--color-border-secondary)',
    backgroundColor: active ? statusC.bg : 'var(--color-bg-tertiary)',
    cursor: draggable ? 'grab' : 'pointer',
    opacity: drag.isDragging ? 0.4 : 1,
    outline: drop.isOver ? '2px solid var(--color-accent-blue)' : 'none',
    // #1 (260616-bd6) — single compact row: [clock] [site] [→ dest] [bbl] [badge].
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  const bblRounded = job.capturedBbl != null ? Math.round(job.capturedBbl) : null;

  // Quick 260721 (#3) — rich hover tooltip rows. Only present values render; this
  // carries the full detail the compact row truncates/omits (Site full, Operator,
  // Status, Destination, Captured BBL, Scheduled), so the row can favor the site.
  const tipRows: ReadonlyArray<{ label: string; value: string }> = [
    { label: 'Site', value: job.siteName },
    ...(job.operatorName !== '' ? [{ label: 'Operator', value: job.operatorName }] : []),
    // Task #32 — the job type + status line heads the tooltip; this row stays for the
    // panel, where the badge shows an abbreviation.
    ...(onCard ? [] : [{ label: 'Status', value: statusFullLabel(job.statusCode) }]),
    ...(job.disposalFacilityName != null && job.disposalFacilityName !== ''
      ? [{ label: 'Destination', value: job.disposalFacilityName }]
      : []),
    ...(bblRounded != null ? [{ label: 'Captured', value: `${bblRounded.toString()} bbl` }] : []),
    ...(job.scheduledAt != null
      ? [{ label: 'Scheduled', value: formatTimestamp(job.scheduledAt) }]
      : []),
    // DEC-299 / Task 18.6 — the operator's note, verbatim, under its own label.
    ...(hasOperatorRequestNote(job.operatorRequestNotes)
      ? [{ label: OPERATOR_NOTE_LABEL, value: job.operatorRequestNotes }]
      : []),
  ];

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            ref={setRefs}
            {...drag.attributes}
            {...drag.listeners}
            style={style}
            role="button"
            tabIndex={0}
            onClick={(): void => onSelect(job.id)}
            onKeyDown={(e): void => {
              if (e.key === 'Enter') onSelect(job.id);
            }}
            onContextMenu={
              onContextMenu === undefined
                ? undefined
                : (e): void => {
                    e.preventDefault();
                    onContextMenu(e.clientX, e.clientY, job.id);
                  }
            }
            onTouchStart={
              onContextMenu === undefined
                ? undefined
                : (e): void => {
                    const t = e.touches[0];
                    if (t === undefined) return;
                    const { clientX, clientY } = t;
                    clearLongPress();
                    longPressTimer.current = setTimeout(() => {
                      onContextMenu(clientX, clientY, job.id);
                    }, LONG_PRESS_MS);
                  }
            }
            onTouchMove={onContextMenu === undefined ? undefined : clearLongPress}
            onTouchEnd={onContextMenu === undefined ? undefined : clearLongPress}
            onTouchCancel={onContextMenu === undefined ? undefined : clearLongPress}
          >
            {onCard && <StatusIcon statusCode={job.statusCode} label={summary} />}
            {/* Task #32 — the type + real status is part of the row's accessible name, so
                a screen reader hears what the dot (or its absence) means. */}
            {onCard && <span style={SR_ONLY}>{summary}</span>}
            {urgencyColor !== null && (
              <span
                aria-label={urgency === 'overdue' ? 'Overdue' : 'Due soon'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: urgencyColor,
                  flexShrink: 0,
                }}
              >
                {/* Quick 260616-tzm (#4) — red (overdue) / amber (due-soon) clock icon. */}
                <Clock size={14} aria-hidden="true" />
              </span>
            )}
            {/* Task #32 — on a driver card, URGENT is a red icon, no word. */}
            {job.isUrgent === true && onCard && (
              <span
                data-testid="urgent-badge"
                role="img"
                aria-label="Urgent"
                style={{
                  display: 'inline-flex',
                  flexShrink: 0,
                  color: 'var(--color-danger)',
                }}
              >
                <TriangleAlert
                  size={14}
                  aria-hidden="true"
                  fill="currentColor"
                  stroke="var(--color-on-status)"
                />
              </span>
            )}
            {/* DEC-146 (Phase 17) — amber URGENT badge (Unassigned panel chips). */}
            {job.isUrgent === true && !onCard && (
              <span
                data-testid="urgent-badge"
                title="Urgent"
                style={{
                  flexShrink: 0,
                  padding: '1px 5px',
                  borderRadius: '12px',
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  color: 'var(--color-warning)',
                  backgroundColor: 'rgba(246, 182, 94, 0.18)',
                  border: '1px solid rgba(246, 182, 94, 0.4)',
                }}
              >
                URGENT
              </span>
            )}
            {/* OPEN-116 — a driver created this load and has not started it within the
                company's unsynced-alert threshold. A dispatcher decides (cancel, or ask the
                driver to sync); it is never cancelled automatically. */}
            {job.notStarted === true && onCard && (
              <span
                data-testid="not-started-badge"
                role="img"
                aria-label="Created by the driver and not started yet"
                style={{
                  display: 'inline-flex',
                  flexShrink: 0,
                  color: 'var(--color-text-tertiary)',
                }}
              >
                <Hourglass size={13} aria-hidden="true" />
              </span>
            )}
            {job.notStarted === true && !onCard && (
              <span
                data-testid="not-started-badge"
                title="Created by the driver and not started yet"
                style={{
                  flexShrink: 0,
                  padding: '1px 5px',
                  borderRadius: '12px',
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  color: 'var(--color-text-tertiary)',
                  backgroundColor: 'rgba(125, 130, 142, 0.16)',
                  border: '1px solid rgba(125, 130, 142, 0.4)',
                }}
              >
                NOT STARTED
              </span>
            )}
            {/* DEC-299 / Task 18.6 — the job carries an OPERATOR's note (API haul request).
                The chip is one line, so it gets a marker; the text is in the tooltip and in
                the detail modal a click opens. Violet, matching OperatorRequestNote. */}
            {hasOperatorRequestNote(job.operatorRequestNotes) && (
              <span
                data-testid="operator-note-badge"
                aria-label={OPERATOR_NOTE_LABEL}
                style={{
                  flexShrink: 0,
                  padding: '1px 5px',
                  borderRadius: '3px',
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  color: OPERATOR_NOTE_ACCENT,
                  border: `1px solid ${OPERATOR_NOTE_ACCENT}`,
                }}
              >
                OP NOTE
              </span>
            )}
            {/* #2 — site name owns the flexible width; it shrinks LAST (low
                flex-shrink) with a small floor so it never starves to 1–2 chars. */}
            <span
              style={{
                flex: '1 1 auto',
                flexShrink: 1,
                minWidth: '4ch',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {job.siteName}
            </span>
            {/* 260624-job-destination — planned destination. #2 — collapses FIRST
                (high flex-shrink, minWidth 0, smaller cap) so it never crowds the
                site name; the full value lives in the hover tooltip. */}
            {/* Task #32 — driver-card rows show the pickup only. */}
            {!onCard && job.disposalFacilityName != null && job.disposalFacilityName !== '' && (
              <span
                style={{
                  flexShrink: 6,
                  minWidth: 0,
                  maxWidth: '35%',
                  fontSize: '11px',
                  color: 'var(--color-text-tertiary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                → {job.disposalFacilityName}
              </span>
            )}
            {/* #4 — captured BBL (compact); only when a capture exists. */}
            {bblRounded != null && (
              <span
                style={{
                  flexShrink: 0,
                  fontSize: '11px',
                  fontWeight: 600,
                  fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {bblRounded.toString()} bbl
              </span>
            )}
            {!onCard && (
              <span style={{ flexShrink: 0 }}>
                <StatusBadge statusCode={job.statusCode} />
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          style={{
            backgroundColor: 'var(--color-popover, var(--color-bg-secondary))',
            border: '1px solid var(--color-border-primary)',
            borderRadius: '6px',
            padding: '8px 10px',
            fontSize: '12px',
            maxWidth: '260px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
            zIndex: 50,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {/* Task #32 — job type + the real status, first. */}
            <div
              data-testid="job-row-summary"
              style={{ fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '2px' }}
            >
              {summary}
            </div>
            {tipRows.map((r) => (
              <TipRow key={r.label} label={r.label} value={r.value} />
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
